import { useEffect, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import type {
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	SmartField_Api_Contracts_Mapping_TowerMappingDto,
	SmartField_Api_Contracts_Mapping_TowerMappingUpdateDto,
	SmartField_Api_Contracts_Sensor_DimSensorDto,
	SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
} from "../../api";
import { TowerHeightConfigPanel } from "./TowerHeightConfigPanel";

type Props = {
	towerName: string;
	rows: SmartField_Api_Contracts_Mapping_TowerMappingDto[];
	els: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	onSaveRow: (id: number, data: SmartField_Api_Contracts_Mapping_TowerMappingUpdateDto) => void;
	onAddRow: (data: SmartField_Api_Contracts_Mapping_TowerMappingUpdateDto) => void;
	onSaveHeightsGroup: (
		sensorIds: number[],
		effectiveFrom: string,
		effectiveTo: string | null,
		data: SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	) => Promise<void>;
	onCancel: () => void;
	isSaving: boolean;
	canEdit: boolean;
};

function RowBlockPicker({
	row,
	els,
	canEdit,
	isSaving,
	onSaveBlock,
}: {
	row: SmartField_Api_Contracts_Mapping_TowerMappingDto;
	els: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	canEdit: boolean;
	isSaving: boolean;
	onSaveBlock: (blockEl: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto) => void;
}) {
	const fieldSelectId = useId();
	const blockSelectId = useId();
	const initialBlockEl = els.find(
		(el) => el.state === "Block" && el.blockId === row.blockId && el.fieldId === row.trialId,
	);
	const [fieldId, setFieldId] = useState<string>(
		initialBlockEl?.fieldId != null ? String(initialBlockEl.fieldId) : "",
	);
	const [blockElId, setBlockElId] = useState<string>(
		initialBlockEl?.id != null ? String(initialBlockEl.id) : "",
	);

	const fields = useMemo(
		() => [...els].filter((el) => el.state === "Field").sort((a, b) => (a.fieldId ?? 0) - (b.fieldId ?? 0)),
		[els],
	);
	const blocksInField = useMemo(
		() =>
			[...els]
				.filter((el) => el.state === "Block" && (fieldId === "" || el.fieldId === Number(fieldId)))
				.sort((a, b) => (a.blockId ?? 0) - (b.blockId ?? 0)),
		[els, fieldId],
	);

	const selectedBlockEl = els.find((el) => String(el.id) === blockElId);
	const isDirty = blockElId !== (initialBlockEl?.id != null ? String(initialBlockEl.id) : "");

	return (
		<div className="flex gap-2">
			<label htmlFor={fieldSelectId} className="sr-only">
				Field
			</label>
			<select
				id={fieldSelectId}
				value={fieldId}
				onChange={(e) => {
					setFieldId(e.target.value);
					setBlockElId("");
				}}
				disabled={!canEdit}
				className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
			>
				<option value="">Field…</option>
				{fields.map((f) => (
					<option key={f.id} value={String(f.fieldId)}>
						{f.friendlyName ?? f.entityIdentifier ?? `Field ${f.fieldId}`}
					</option>
				))}
			</select>
			<label htmlFor={blockSelectId} className="sr-only">
				Block
			</label>
			<select
				id={blockSelectId}
				value={blockElId}
				onChange={(e) => setBlockElId(e.target.value)}
				disabled={!canEdit}
				className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
			>
				<option value="">Block…</option>
				{blocksInField.map((b) => (
					<option key={b.id} value={String(b.id)}>
						{b.friendlyName ?? `Block ${b.blockId}`}
					</option>
				))}
			</select>
			{canEdit && (
				<button
					type="button"
					onClick={() => selectedBlockEl && onSaveBlock(selectedBlockEl)}
					disabled={!isDirty || !selectedBlockEl || isSaving}
					className="px-2 py-1 text-xs bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-40 disabled:cursor-not-allowed"
					title="Save block"
				>
					Save
				</button>
			)}
		</div>
	);
}

export function TowerGroupConfigModal({
	towerName,
	rows,
	els,
	sensors,
	onSaveRow,
	onAddRow,
	onSaveHeightsGroup,
	onCancel,
	isSaving,
	canEdit,
}: Props) {
	const blockMappedRows = rows.filter((r) => r.blockId != null);
	const [selectedRowId, setSelectedRowId] = useState<string>(
		blockMappedRows[0]?.id != null ? String(blockMappedRows[0].id) : "",
	);
	const selectedRow = blockMappedRows.find((r) => String(r.id) === selectedRowId);

	useEffect(() => {
		if (selectedRow == null && blockMappedRows[0]?.id != null) {
			setSelectedRowId(String(blockMappedRows[0].id));
		}
	}, [selectedRow, blockMappedRows]);

	return (
		<div className="p-5 space-y-5">
			<h3 className="text-sm font-semibold text-gray-900">Configure Tower {towerName}</h3>

			<div className="space-y-3">
				<h4 className="text-xs font-semibold text-gray-500 uppercase">Mappings</h4>
				<table className="w-full text-xs border-collapse">
					<thead>
						<tr className="border-b border-gray-200">
							<th className="text-left px-2 py-1 text-gray-400 font-medium">Trial</th>
							<th className="text-left px-2 py-1 text-gray-400 font-medium">Block</th>
							<th className="text-left px-2 py-1 text-gray-400 font-medium">Plots</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr key={row.id} className="border-b border-gray-100">
								<td className="px-2 py-1">{row.trialId}</td>
								<td className="px-2 py-1 min-w-[220px]">
									{canEdit ? (
										<RowBlockPicker
											row={row}
											els={els}
											canEdit={canEdit}
											isSaving={isSaving}
											onSaveBlock={(blockEl) => {
												if (blockEl.blockId == null || blockEl.fieldId == null) {
													return;
												}
												onSaveRow(row.id!, {
													year: row.year!,
													towerName: row.towerName!,
													trialId: blockEl.fieldId,
													blockId: blockEl.blockId,
												});
											}}
										/>
									) : row.blockId != null ? (
										row.blockId
									) : (
										<span className="text-gray-400 italic">Unmapped</span>
									)}
								</td>
								<td className="px-2 py-1 text-gray-500">
									{row.plotIds && row.plotIds.length > 0 ? row.plotIds.join(", ") : "—"}
								</td>
							</tr>
						))}
					</tbody>
				</table>
				{canEdit && (
					<button
						type="button"
						onClick={() => {
							const trial = blockMappedRows[0]?.trialId;
							const year = blockMappedRows[0]?.year;
							const blockId = blockMappedRows[0]?.blockId;
							if (trial == null || year == null || blockId == null) {
								toast.error("No block-mapped row to copy trial/block context from");
								return;
							}
							onAddRow({ year, towerName, trialId: trial, blockId });
						}}
						disabled={isSaving}
						className="text-xs px-1.5 py-0.5 text-smartfield hover:bg-blue-50 rounded"
					>
						+ Add row
					</button>
				)}
			</div>

			<div className="space-y-3 border-t border-gray-100 pt-4">
				<h4 className="text-xs font-semibold text-gray-500 uppercase">Heights</h4>
				{blockMappedRows.length === 0 ? (
					<p className="text-xs text-gray-400">No block-mapped row to configure heights for.</p>
				) : (
					<>
						{blockMappedRows.length > 1 && (
							<div>
								<label htmlFor="height-row" className="block text-xs font-medium text-gray-700 mb-1">
									Configure heights for
								</label>
								<select
									id="height-row"
									value={selectedRowId}
									onChange={(e) => setSelectedRowId(e.target.value)}
									className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900"
								>
									{blockMappedRows.map((r) => (
										<option key={r.id} value={String(r.id)}>
											Trial {r.trialId} / Block {r.blockId}
										</option>
									))}
								</select>
							</div>
						)}
						{selectedRow?.blockId != null && selectedRow.trialId != null && (
							<TowerHeightConfigPanel
								towerName={towerName}
								trialId={selectedRow.trialId}
								blockId={selectedRow.blockId}
								els={els}
								sensors={sensors}
								onSaveHeightsGroup={onSaveHeightsGroup}
								canEdit={canEdit}
								isSaving={isSaving}
							/>
						)}
					</>
				)}
			</div>

			<div className="pt-2">
				<button
					type="button"
					onClick={onCancel}
					className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
				>
					Cancel
				</button>
			</div>
		</div>
	);
}
